import { useId, useState, type ReactNode } from "react";
import Reveal from "../motion/Reveal";
import markerUrl from "../assets/resume/marker.svg";
import downloadUrl from "../assets/resume/download.svg";
import "./ResumePage.css";

type Metric = { value: string; label: string };

type Role = {
  company: string;
  title: string;
  scope: string;
  metrics: Metric[];
  highlights: string[];
  bullets: ReactNode[];
};

const ROLES: Role[] = [
  {
    company: "Cadent : January 2025 – October 2026",
    title: "Director, User experience",
    scope:
      "Directed UX strategy across Cadent's DSP, SSP, support experiences, and emerging AI products, aligning complex enterprise workflows with customer needs and business priorities while remaining hands-on in product design.",
    metrics: [
      { value: "32%", label: "Reduction in task completion time" },
      { value: "15%", label: "Reduction in design-to-development cycle times" },
    ],
    highlights: [
      "Conceived and drove the AI Media Planner initiative from initial hypothesis through research, product strategy, AI chat experience design, and interactive prototyping.",
      "Originated an AI-assisted RFP Copilot, owning discovery, user research, interaction design, and prototyping. Refined human-in-the-loop workflows through eight usability-testing sessions to balance automation, accuracy, and user control.",
      "Led and developed a product design team, managing two direct reports and mentoring additional designers.",
    ],
    bullets: [
      "Directed UX strategy across Cadent's DSP, SSP, support experiences, and emerging AI products, aligning complex enterprise workflows with customer needs and business priorities while remaining hands-on in product design.",
      "Conceived and drove the AI Media Planner initiative from initial hypothesis through research, product strategy, AI chat experience design, and interactive prototyping—establishing a vision for AI-assisted media planning.",
      "Originated an AI-assisted RFP Copilot, owning discovery, user research, interaction design, and prototyping. Refined human-in-the-loop workflows through eight usability-testing sessions to balance automation, accuracy, and user control.",
      <>
        Led enterprise workflow modernization initiatives that{" "}
        <strong>reduced task completion time by 32%</strong>, simplifying
        complex experiences while preserving critical functionality.
      </>,
      <>
        Co-led the development of a shared enterprise design system,
        established component governance and documentation, and introduced
        weekly and quarterly DesignOps ceremonies, contributing to a{" "}
        <strong>15% reduction in design-to-development cycle times</strong>.
      </>,
      "Led and developed a product design team, managing two direct reports and mentoring additional designers. Oversaw performance, professional growth, design priorities, and delivery across multiple enterprise initiatives during organizational transformation.",
    ],
  },
  {
    company: "AdTheorent : July 2024 - December 2024",
    title: "Associate Director, Product Design",
    scope:
      "Led UX integration of AdTheorent's redesigned advertising platform into the broader Cadent product ecosystem following acquisition, identifying critical user journeys, interaction patterns, and cross-platform requirements.",
    metrics: [
      {
        value: "57%",
        label: "Improvement in design-to-development predictability",
      },
    ],
    highlights: [
      "Directed a subsequent enterprise redesign toward a unified experience across Cadent and AdTheorent products, reconciling distinct product capabilities, established workflows, and shared design principles.",
      "Partnered with Product, Engineering, and executive stakeholders to define a cohesive product direction while managing two designers through organizational transition.",
    ],
    bullets: [
      "Led UX integration of AdTheorent's redesigned advertising platform into the broader Cadent product ecosystem following acquisition, identifying critical user journeys, interaction patterns, and cross-platform requirements.",
      "Directed a subsequent enterprise redesign toward a unified experience across Cadent and AdTheorent products, reconciling distinct product capabilities, established workflows, and shared design principles.",
      <>
        Partnered with Product, Engineering, and executive stakeholders to
        define a cohesive product direction while managing two designers
        through organizational transition. Established design governance and
        delivery practices that{" "}
        <strong>improved design-to-development predictability by 57%</strong>.
      </>,
    ],
  },
  {
    company: "AdTheorent : January 2023 - June 2024",
    title: "Senior Product Designer",
    scope:
      "Led the comprehensive transformation of AdTheorent's enterprise advertising platform from an internally focused application into a product designed to support external customers. Collaborated directly with the founder, CTO, and two VPs of Product to define the vision and experience.",
    metrics: [
      {
        value: "40+",
        label: "Prototype screens built in approximately two weeks",
      },
      {
        value: "$56M",
        label: "Revenue associated with HABi during its first two years",
      },
    ],
    highlights: [
      "Reimagined the platform's information architecture, product naming, account navigation, and permission-aware workflows, fundamentally changing how users accessed, understood, and moved through the software.",
      "Designed HABi, a privacy-first, HIPAA-compliant healthcare audience builder. Simplified complex audience discovery, segmentation, and data-navigation workflows.",
      "Established scalable interaction patterns and design-system foundations in partnership with Engineering, contributing to 20% greater development efficiency and 30% less QA rework.",
    ],
    bullets: [
      "Led the comprehensive transformation of AdTheorent's enterprise advertising platform from an internally focused application into a product designed to support external customers. Collaborated directly with the founder, CTO, and two VPs of Product to define the vision and experience.",
      "Reimagined the platform's information architecture, product naming, account navigation, and permission-aware workflows, fundamentally changing how users accessed, understood, and moved through the software.",
      <>
        Designed a high-fidelity, end-to-end prototype spanning{" "}
        <strong>40+ screens and multiple user journeys in approximately two weeks</strong>. Presented the vision to executives, stakeholders, and shareholders,
        securing rapid alignment and buy-in for the redesign.
      </>,
      <>
        Designed HABi, a privacy-first, HIPAA-compliant healthcare audience
        builder associated with more than{" "}
        <strong>$56M in revenue during its first two years</strong>. Simplified
        complex audience discovery, segmentation, and data-navigation
        workflows.
      </>,
      "Established scalable interaction patterns and design-system foundations in partnership with Engineering, contributing to 20% greater development efficiency and 30% less QA rework.",
    ],
  },
  {
    company: "AdTheorent : November 2021 - December 2022",
    title: "Product Designer",
    scope:
      "Joined as AdTheorent's first in-house Product Designer to demonstrate the value of dedicated UX.",
    metrics: [{ value: "15%", label: "Increase in feature adoption" }],
    highlights: [
      "Earned a direct report within six months, doubling the size of the design function and establishing a foundation for its continued growth.",
      "Introduced foundational UX practices, including user interviews, usability testing, workflow analysis, and research-informed design. Created the organization's first reusable design library and interaction standards.",
    ],
    bullets: [
      "Joined as AdTheorent's first in-house Product Designer to demonstrate the value of dedicated UX. Earned a direct report within six months, doubling the size of the design function and establishing a foundation for its continued growth.",
      "Introduced foundational UX practices, including user interviews, usability testing, workflow analysis, and research-informed design. Created the organization's first reusable design library and interaction standards.",
      "Redesigned approximately 30% of the legacy application within six months, introducing new capabilities and improved workflows that contributed to a 15% increase in feature adoption.",
    ],
  },
];

type EarlierRole = {
  company: string;
  title: string;
  bullets: string[];
};

const EARLIER: EarlierRole[] = [
  {
    company: "Maximus Real Estate Partners : October 2020 – January 2022",
    title: "Human Experience Ambassador",
    bullets: [
      "Designed and launched five community-facing websites serving 5,000+ residents and staff; conducted interviews, journey mapping, workshops, and qualitative and quantitative analysis.",
    ],
  },
  {
    company: "Maximus Real Estate Partners : January 2018 – October 2020",
    title: "Project Associate",
    bullets: [
      "Designed accessible outreach websites and developed communications for community and organizational initiatives.",
    ],
  },
];

const SKILLS = [
  {
    title: "Product & UX",
    items: [
      "Product strategy",
      "Enterprise SaaS UX",
      "User research",
      "Information architecture",
      "Accessibility",
      "Prototyping",
      "Interaction design",
      "Usability testing",
      "Design systems",
      "Complex workflows",
    ],
  },
  {
    title: "Leadership & Systems",
    items: [
      "Design leadership",
      "Design Ops",
      "People management",
      "Cross-functional alignment",
      "Platform consolidation",
      "Design governance",
      "Strategic planning",
      "Product vision",
      "Executive communication & storytelling",
      "Mentorship",
    ],
  },
  {
    title: "AI Experience",
    items: [
      "AI-assisted workflows",
      "Human-in-the-loop experiences",
      "AI product discovery & prototyping",
      "AI product strategy",
      "Human-centered AI product design",
      "Conversational AI interactions",
    ],
  },
];

function Kicker({ id, tone = "ink" }: { id: string; tone?: "ink" | "card" }) {
  return (
    <p className={`section-kicker resume-kicker is-${tone}`}>
      <span>{id}</span>
      <span className="section-rule" aria-hidden="true" />
    </p>
  );
}

function EarlierRoleBlock({ role }: { role: EarlierRole }) {
  return (
    <article className="resume-role">
      <div className="resume-rail" aria-hidden="true">
        <img src={markerUrl} alt="" width={16} height={16} />
        <span className="resume-line" />
      </div>
      <div className="resume-role-body">
        <p className="resume-company">{role.company}</p>
        <h3>{role.title}</h3>
        <ul className="resume-earlier-list">
          {role.bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      </div>
    </article>
  );
}

function RoleBlock({ role }: { role: Role }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const single = role.metrics.length === 1;

  return (
    <article className="resume-role">
      <div className="resume-rail" aria-hidden="true">
        <img src={markerUrl} alt="" width={16} height={16} />
        <span className="resume-line" />
      </div>
      <div className="resume-role-body">
        <p className="resume-company">{role.company}</p>
        <h3>{role.title}</h3>
        <p className="resume-scope">{role.scope}</p>
        <div className={`resume-metrics${single ? " is-single" : ""}`}>
          {role.metrics.map((metric) => (
            <div className="resume-metric" key={metric.value + metric.label}>
              <p className="resume-metric-value">{metric.value}</p>
              <p className="resume-metric-label">{metric.label}</p>
            </div>
          ))}
        </div>
        <p className="resume-selected-label">Selected contributions</p>
        <ul className="resume-highlights">
          {role.highlights.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <div
          className={`resume-full${open ? " is-open" : ""}`}
          id={panelId}
          aria-hidden={open ? undefined : true}
        >
          <div className="resume-full-inner">
            <ul>
              {role.bullets.map((bullet, index) => (
                <li key={index}>{bullet}</li>
              ))}
            </ul>
          </div>
        </div>
        <button
          type="button"
          className="resume-role-toggle"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((value) => !value)}
        >
          <span>{open ? "Show less" : "View full role details"}</span>
          <svg
            className={open ? "is-open" : undefined}
            width="12"
            height="12"
            viewBox="0 0 12 12"
            aria-hidden="true"
          >
            <path
              d="M2.5 4.5 6 8 9.5 4.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </article>
  );
}

export default function ResumePage() {
  const [earlierOpen, setEarlierOpen] = useState(false);
  const hasEarlier = EARLIER.length > 0;

  return (
    <div className="resume-page" id="resume">
      <main>
        <header className="resume-heading">
          <Reveal>
            <Kicker id="03" />
          </Reveal>
          <Reveal variant="editorial" delay={1}>
            <h1 className="resume-title">Résumé</h1>
          </Reveal>
          <Reveal as="p" variant="stagger" delay={2} className="resume-subtitle">
            <span>Product design leader</span>
            <span>UX strategy</span>
            <span>Hands-on design</span>
          </Reveal>
        </header>

        <div className="resume-layout">
          <div className="resume-main">
            <section className="resume-block" aria-labelledby="resume-summary">
              <Reveal>
                <Kicker id="03.01" />
                <h2 id="resume-summary">Summary</h2>
              </Reveal>
              <Reveal delay={1}>
                <p className="resume-summary">
                  Product design and UX leader with 9+ years of experience,
                  including 5 years transforming complex enterprise SaaS
                  platforms. Progressed from establishing AdTheorent&apos;s{" "}
                  <strong>first in-house UX function to Director of UX</strong>, leading platform transformations, product strategy, and AI
                  initiatives. Combines 4+ years of people leadership with
                  hands-on expertise in research, systems thinking, interaction
                  design, and high-fidelity prototyping.
                </p>
              </Reveal>
            </section>

            <section className="resume-block" aria-labelledby="resume-experience">
              <Reveal>
                <Kicker id="03.02" />
                <h2 id="resume-experience">Experience</h2>
              </Reveal>
              <div className="resume-roles">
                {ROLES.map((role, index) => (
                  <Reveal key={role.company} delay={index + 1}>
                    <RoleBlock role={role} />
                  </Reveal>
                ))}
              </div>
              <div className="resume-earlier-wrap">
                <button
                  type="button"
                  className="resume-earlier"
                  aria-expanded={hasEarlier ? earlierOpen : false}
                  aria-controls={hasEarlier ? "earlier-experience" : undefined}
                  aria-disabled={hasEarlier ? undefined : true}
                  onClick={() => {
                    if (hasEarlier) setEarlierOpen((open) => !open);
                  }}
                >
                  <svg
                    className={earlierOpen ? "is-open" : undefined}
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    aria-hidden="true"
                  >
                    <path
                      d="M4 2.5 7.5 6 4 9.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {earlierOpen ? "Hide Earlier Experience" : "View Earlier Experience"}
                </button>
                {hasEarlier ? (
                  <div
                    className={`resume-earlier-panel${earlierOpen ? " is-open" : ""}`}
                    id="earlier-experience"
                    aria-hidden={earlierOpen ? undefined : true}
                  >
                    <div className="resume-earlier-panel-inner">
                      <div className="resume-roles">
                        {EARLIER.map((role) => (
                          <EarlierRoleBlock key={role.title} role={role} />
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </section>
          </div>

          <div className="resume-side">
            <a
              className="resume-download"
              href={`${import.meta.env.BASE_URL}Arielle_Schussler_Resume.pdf`}
              download="Arielle_Schussler_Resume.pdf"
            >
              <span>Download Résumé</span>
              <img src={downloadUrl} alt="" width={14} height={14} />
            </a>

            <section className="resume-card panel" aria-labelledby="resume-skills">
              <Reveal>
                <Kicker id="03.03" tone="card" />
                <h2 id="resume-skills">Skills</h2>
              </Reveal>
              <div className="resume-skill-groups">
                {SKILLS.map((group, index) => (
                  <Reveal key={group.title} className="resume-skill-group" delay={index + 1}>
                    <h3>{group.title}</h3>
                    <ul>
                      {group.items.map((item) => (
                        <li key={item} className="pill">{item}</li>
                      ))}
                    </ul>
                  </Reveal>
                ))}
              </div>
            </section>

            <section className="resume-card panel" aria-labelledby="resume-education">
              <Reveal delay={1}>
                <Kicker id="03.04" tone="card" />
                <h2 id="resume-education">Education</h2>
                <div className="resume-facts">
                  <div>
                    <p>Saint Mary’s College of California 2017</p>
                    <h3>MFA Creative Writing</h3>
                  </div>
                  <div>
                    <p>University of California, Berkeley 2013</p>
                    <h3>BA Philosophy</h3>
                  </div>
                </div>
              </Reveal>
            </section>

            <section className="resume-card panel" aria-labelledby="resume-certifications">
              <Reveal delay={2}>
                <Kicker id="03.05" tone="card" />
                <h2 id="resume-certifications">Certifications</h2>
                <div className="resume-facts">
                  <div>
                    <p>Maven 2025</p>
                    <h3>Advanced AI UX</h3>
                  </div>
                  <div>
                    <p>Design Lab 2025</p>
                    <h3>Advanced Figma</h3>
                  </div>
                </div>
              </Reveal>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
